import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api from "../../services/api";
import { useRealtimePrices } from "../../hooks/useRealtimePrices";
import { subscribeSymbols, unsubscribeDelayed } from "../../ws/marketSubscriptions";
import "./StockDetailsPage.css";
import TradingViewChart from "../../components/TradingViewChart/TradingViewChart";
import StockNewsWidget from "../../components/StockNewsWidget/StockNewsWidget";
import StockAnnouncementsWidget from "../../components/StockAnnouncementsWidget/StockAnnouncementsWidget";

/* ─── Icons ───────────────────────────────────── */
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* ─── Helpers ─────────────────────────────────── */
const formatCurrency = (val) => {
  if (val == null || isNaN(val)) return "—";
  const num = Number(val);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatNumber = (val) => {
  if (val == null || isNaN(val)) return "—";
  const num = Number(val);
  if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString("en-IN");
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ─── Reusable Components ─────────────────────── */
const DetailItem = ({ label, value, mono, large, colorClass, children }) => (
  <div className="sd-detail-item">
    <span className="sd-detail-label">{label}</span>
    {children ? (
      children
    ) : (
      <span className={`sd-detail-value${mono ? " mono" : ""}${large ? " large" : ""}${colorClass ? ` ${colorClass}` : ""}`}>
        {value ?? "—"}
      </span>
    )}
  </div>
);

const Section = ({ title, icon, iconColor = "purple", fullWidth, headerRight, className, children }) => (
  <div className={`sd-section${fullWidth ? " full-width" : ""}${className ? ` ${className}` : ""}`}>
    <div className="sd-section-header">
      <div className={`sd-section-icon ${iconColor}`}>
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <h3 className="sd-section-title">{title}</h3>
      {headerRight && <div className="sd-section-header-right">{headerRight}</div>}
    </div>
    <div className="sd-section-body">{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════ */
const StockDetailsPage = () => {
  const location = useLocation();
  const { prices } = useRealtimePrices();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Stock data sections
  const [marketData, setMarketData] = useState(null);
  const [dealsData, setDealsData] = useState([]);
  const [deliveryData, setDeliveryData] = useState(null);
  const [high52wData, setHigh52wData] = useState(null);
  const [low52wData, setLow52wData] = useState(null);
  const [peRatioData, setPeRatioData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [tradeRecommendationsData, setTradeRecommendationsData] = useState([]);
  const [scanHitsData, setScanHitsData] = useState([]);
  const [marketStatus, setMarketStatus] = useState(null);

  // Deals pagination
  const [dealsPage, setDealsPage] = useState(1);
  const dealsPerPage = 5;

  // News & Announcements pagination
  const [announcementsPage, setAnnouncementsPage] = useState(1);
  const itemsPerPage = 5;

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch market status
  useEffect(() => {
    const fetchMarketStatus = async () => {
      try {
        const res = await api.get("/trading/market-open");
        setMarketStatus(res?.data || null);
      } catch {
        // Ignore
      }
    };
    fetchMarketStatus();
  }, []);

  // Subscribe to WebSocket for selected stock
  const tokensToSubscribe = useMemo(() => {
    if (!selectedStock) return [];
    const sym = selectedStock.symbol || selectedStock.tradingsymbol;
    return sym ? [sym.toUpperCase().replace(/-EQ$/, "")] : [];
  }, [selectedStock]);

  useEffect(() => {
    if (tokensToSubscribe.length === 0) return;
    const page = "StockDetails";
    const context = "LivePrice";
    subscribeSymbols(tokensToSubscribe, page, context);
    return () => {
      unsubscribeDelayed(tokensToSubscribe, page, context);
    };
  }, [tokensToSubscribe]);

  // Get live price for selected stock
  const livePrice = useMemo(() => {
    if (!selectedStock) return null;
    const sym = (selectedStock.symbol || "").toUpperCase().replace(/-EQ$/, "");
    const token = selectedStock.token || selectedStock.ticker;
    return prices[token] || prices[sym] || prices[`${sym}-EQ`] || null;
  }, [selectedStock, prices]);

  // Search handler with debounce
  const handleSearchInput = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/trading/search?query=${encodeURIComponent(value.trim())}`);
        const data = res?.data || [];
        setSuggestions(Array.isArray(data) ? data.slice(0, 10) : []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  }, []);

  // Select stock from suggestions or search
  const handleSelectStock = useCallback(async (stock) => {
    setShowSuggestions(false);
    setSearchQuery(stock.symbol || stock.name || "");
    setLoading(true);
    setError(null);
    setSelectedStock(null);
    setSearched(true);
    setDealsPage(1);

    // Reset all data
    setMarketData(null);
    setDealsData([]);
    setDeliveryData(null);
    setHigh52wData(null);
    setLow52wData(null);
    setPeRatioData(null);
    setAnnouncements([]);
    setNewsItems([]);
    setTradeRecommendationsData([]);
    setScanHitsData([]);

    const symbol = stock.symbol || stock.tradingsymbol;
    const baseSymbol = symbol ? symbol.replace(/-EQ$/i, "") : "";
    const token = stock.ticker || stock.token;

    try {
      // Set basic stock info immediately
      setSelectedStock(stock);

      // Fetch all data in parallel
      const promises = [];

      // 1. Market trade data
      promises.push(
        api.get(`/market_import_data/data?symbol=${encodeURIComponent(baseSymbol)}&limit=1`)
          .then(res => {
            const data = res?.data?.data || res?.data || [];
            if (Array.isArray(data) && data.length > 0) {
              const mktData = data[0];
              const matchData = {
                open: mktData.open,
                high: mktData.high,
                low: mktData.low,
                close: mktData.close,
                prev_close: mktData.previous_close || mktData.close,
                ltp: mktData.last_price || mktData.close,
                trade_volume: mktData.trade_volume,
                volume: mktData.trade_volume,
                trade_date: mktData.trade_date,
                avg_price: mktData.avg_price || mktData.average_price,
                settlement_price: mktData.settlement_price,
                total_value: mktData.total_value,
                trade_counts: mktData.trade_counts
              };
              setMarketData(matchData);
              setSelectedStock(prev => ({ ...prev, ...matchData }));
            }
          })
          .catch(() => {})
      );

      // 2. Deals data
      promises.push(
        api.get(`/deals_import_data/data?symbol=${encodeURIComponent(baseSymbol)}&limit=20`)
          .then(res => {
            const deals = res?.data?.data || res?.data || [];
            setDealsData(Array.isArray(deals) ? deals : []);
          })
          .catch(() => setDealsData([]))
      );

      // 3. Delivery data
      promises.push(
        api.get(`/delivery_import_data/data?symbol=${encodeURIComponent(baseSymbol)}&limit=5`)
          .then(res => {
            const data = res?.data?.data || res?.data || [];
            setDeliveryData(Array.isArray(data) && data.length > 0 ? data[0] : null);
          })
          .catch(() => setDeliveryData(null))
      );

      // 4. 52-week high
      promises.push(
        api.get(`/high52w_import_data/data?symbol=${encodeURIComponent(baseSymbol)}&limit=1`)
          .then(res => {
            const data = res?.data?.data || res?.data || [];
            setHigh52wData(Array.isArray(data) && data.length > 0 ? data[0] : null);
          })
          .catch(() => setHigh52wData(null))
      );

      // 5. 52-week low
      promises.push(
        api.get(`/low52w_import_data/data?symbol=${encodeURIComponent(baseSymbol)}&limit=1`)
          .then(res => {
            const data = res?.data?.data || res?.data || [];
            setLow52wData(Array.isArray(data) && data.length > 0 ? data[0] : null);
          })
          .catch(() => setLow52wData(null))
      );

      // 6. PE ratio
      promises.push(
        api.get(`/pe_ratio_import_data/data?symbol=${encodeURIComponent(baseSymbol)}&limit=1`)
          .then(res => {
            const data = res?.data?.data || res?.data || [];
            setPeRatioData(Array.isArray(data) && data.length > 0 ? data[0] : null);
          })
          .catch(() => setPeRatioData(null))
      );

      // 8. Corporate announcements (filter by company name via `title` param)
      const validBaseSymbol = baseSymbol && baseSymbol !== "undefined" ? baseSymbol : "";
      const companyName = stock.name || validBaseSymbol;
      
      if (companyName) {
        promises.push(
          api.get('/corporate-announcements/raw', { params: { limit: 100, title: companyName } })
            .then(res => {
              const data = res?.data?.data || res?.data || [];
              let allAnns = Array.isArray(data) ? data : [];
              setAnnouncements(allAnns);
              setAnnouncementsPage(1);
            })
            .catch(() => setAnnouncements([]))
        );
      }

      // 9. Trade Recommendations
      promises.push(
        api.get(`/traderecommendation?search=${encodeURIComponent(baseSymbol)}&limit=10`)
          .then(res => {
            const rawData = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
            const data = Array.isArray(rawData) ? rawData.map(trade => ({
              ...trade,
              tradeTypeName: trade.tradeTypeName || trade.tradetypename,
              status: trade.status || trade.recommendationstatus,
              recoPriceLow: trade.recoPriceLow || trade.recopricelow,
              recoPriceHigh: trade.recoPriceHigh || trade.recopricehigh,
              ltp: trade.ltp || trade.value,
              exitPriceLow: trade.exitPriceLow || trade.exitpricelow,
              targetOne: trade.targetOne || trade.targetone || trade.targetPrice || trade.targetprice,
              targetTwo: trade.targetTwo || trade.targettwo,
              targetOneStatus: trade.targetOneStatus || trade.targetonestatus,
              targetTwoStatus: trade.targetTwoStatus || trade.targettwostatus,
              stopLoss: trade.stopLoss || trade.stoploss,
              actual_profit: trade.actual_profit || trade.actualprofit,
              createdAt: trade.createdAt || trade.createdat || trade.recoDate || trade.recodate,
            })) : [];
            setTradeRecommendationsData(data);
          })
          .catch(() => setTradeRecommendationsData([]))
      );

      // 10. News (all published & unpublished, filtered by stock)
      // Try multiple search strategies and merge results for maximum coverage
      const validToken = token && token !== "undefined" ? token : "";
      const newsSearches = [];
      
      // Strategy 1: Search by company name (e.g., "Avenue Supermarts" for DMART)
      if (companyName) {
        newsSearches.push(api.get('/newsfeed', { params: { limit: 100, title: companyName } }).catch(() => ({ data: [] })));
      }
      // Strategy 2: Search by symbol (e.g., "DMART")
      if (validBaseSymbol && validBaseSymbol !== companyName) {
        newsSearches.push(api.get('/newsfeed', { params: { limit: 100, title: validBaseSymbol } }).catch(() => ({ data: [] })));
      }
      // Strategy 3: Search by stock token
      if (validToken) {
        newsSearches.push(api.get('/newsfeed', { params: { limit: 100, stock: validToken } }).catch(() => ({ data: [] })));
      }
      
      if (newsSearches.length > 0) {
        promises.push(
          Promise.all(newsSearches)
            .then(results => {
              // Merge all results and deduplicate by news_id
              const allNewsMap = new Map();
              results.forEach(res => {
                const data = res?.data?.data || res?.data || [];
                const items = Array.isArray(data) ? data : [];
                items.forEach(item => {
                  const key = item.news_id || item.id || item.title;
                  if (!allNewsMap.has(key)) {
                    allNewsMap.set(key, item);
                  }
                });
              });
              const allItems = Array.from(allNewsMap.values());
              
              console.log('[StockDetails] News fetch results:', { companyName, validBaseSymbol, validToken, totalFound: allItems.length });
              setNewsItems(allItems);
              
              // Extract published announcements and append them to the announcements widget
              const isAnnouncement = (item) => {
                const catName = (item.news_category_name || item.category || '').toLowerCase();
                return catName.includes('announcement') || catName.includes('event') || catName.includes('corporate');
              };
              const publishedAnns = allItems.filter(item => isAnnouncement(item));
              
              if (publishedAnns.length > 0) {
                  setAnnouncements(prev => {
                      return [...prev, ...publishedAnns];
                  });
              }
            })
            .catch(() => setNewsItems([]))
        );
      }

      // 11. Scan Hits for this stock
      promises.push(
        api.get(`/communitysequence/scan-results?symbol=${encodeURIComponent(baseSymbol)}`)
          .then(res => {
            const data = res?.data || [];
            if (Array.isArray(data)) {
              // Deduplicate by scan_name and date so historical hits are preserved
              const uniqueScans = Array.from(new Map(data.map(item => {
                const dateKey = item.scan_timestamp ? new Date(item.scan_timestamp).toISOString().split('T')[0] : 'unknown';
                return [`${item.scan_name}-${dateKey}`, item];
              })).values());
              setScanHitsData(uniqueScans);
            } else {
              setScanHitsData([]);
            }
          })
          .catch(() => setScanHitsData([]))
      );

      await Promise.allSettled(promises);

    } catch (err) {
      setError(err?.message || "Failed to fetch stock details");
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle passed state from other pages
  useEffect(() => {
    if (location.state?.selectedStock) {
      handleSelectStock(location.state.selectedStock);
      // clear the state so it doesn't run again on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, handleSelectStock]);

  // Direct search (when pressing Enter or Search button)
  const handleDirectSearch = useCallback(async () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    setShowSuggestions(false);
    setLoading(true);
    setError(null);
    setSelectedStock(null);
    setSearched(true);

    try {
      const res = await api.get(`/trading/search?query=${encodeURIComponent(trimmed)}`);
      const data = res?.data || [];
      if (Array.isArray(data) && data.length > 0) {
        await handleSelectStock(data[0]);
      } else {
        setError(`No stock found matching "${trimmed}". Try a different symbol or name.`);
        setLoading(false);
      }
    } catch (err) {
      setError(err?.message || "Search failed. Please try again.");
      setLoading(false);
    }
  }, [searchQuery, handleSelectStock]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleDirectSearch();
  };

  const handleClear = () => {
    setSearchQuery("");
    setSelectedStock(null);
    setError(null);
    setSearched(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setMarketData(null);
    setDealsData([]);
    setDeliveryData(null);
    setHigh52wData(null);
    setLow52wData(null);
    setPeRatioData(null);
    setAnnouncements([]);
    setNewsItems([]);
    setTradeRecommendationsData([]);
  };

  // Compute derived values
  const currentLTP = useMemo(() => {
    if (livePrice?.price) return livePrice.price;
    if (selectedStock?.ltp) return Number(selectedStock.ltp);
    if (marketData?.ltp) return Number(marketData.ltp);
    return null;
  }, [livePrice, selectedStock, marketData]);

  const prevClose = useMemo(() => {
    if (livePrice?.prevClose) return livePrice.prevClose;
    if (marketData?.prev_close) return Number(marketData.prev_close);
    return null;
  }, [livePrice, marketData]);

  const priceChange = useMemo(() => {
    if (livePrice?.change != null) return livePrice.change;
    if (currentLTP && prevClose) return currentLTP - prevClose;
    return null;
  }, [livePrice, currentLTP, prevClose]);

  const priceChangePercent = useMemo(() => {
    if (livePrice?.changePercent != null) return livePrice.changePercent;
    if (priceChange != null && prevClose) return (priceChange / prevClose) * 100;
    return null;
  }, [livePrice, priceChange, prevClose]);

  const changeDirection = priceChange > 0 ? "up" : priceChange < 0 ? "down" : "neutral";

  // 52-week range position calculation
  const range52wPosition = useMemo(() => {
    const high = high52wData?.high_52w ? Number(high52wData.high_52w) : null;
    const low = low52wData?.low_52w ? Number(low52wData.low_52w) : null;
    if (!high || !low || !currentLTP || high === low) return 50;
    return Math.min(100, Math.max(0, ((currentLTP - low) / (high - low)) * 100));
  }, [high52wData, low52wData, currentLTP]);

  // Day range position calculation
  const dayRangePosition = useMemo(() => {
    const high = marketData?.high ? Number(marketData.high) : selectedStock?.high ? Number(selectedStock.high) : null;
    const low = marketData?.low ? Number(marketData.low) : selectedStock?.low ? Number(selectedStock.low) : null;
    if (!high || !low || !currentLTP || high === low) return 50;
    return Math.min(100, Math.max(0, ((currentLTP - low) / (high - low)) * 100));
  }, [marketData, selectedStock, currentLTP]);

  // Delivery percentage
  const deliveryPct = useMemo(() => {
    if (!deliveryData) return null;
    const traded = Number(deliveryData.traded_qty);
    const delivery = Number(deliveryData.delivery_qty);
    if (!traded || isNaN(traded) || traded === 0) return null;
    return ((delivery / traded) * 100).toFixed(1);
  }, [deliveryData]);

  const paginatedDeals = dealsData.slice((dealsPage - 1) * dealsPerPage, dealsPage * dealsPerPage);
  const totalDealsPages = Math.ceil(dealsData.length / dealsPerPage);

  const totalAnnouncementsPages = Math.ceil(announcements.length / itemsPerPage);
  const paginatedAnnouncements = announcements.slice((announcementsPage - 1) * itemsPerPage, announcementsPage * itemsPerPage);

  return (
    <div className="sd-page">
      {/* ─── Header ─────────────────────────── */}
      <div className="sd-header">
        <div className="sd-header-left">
          <h1>Stock Details</h1>
          <p>Search and analyze comprehensive stock information — prices, deals, deliveries, ratios, announcements & more</p>
        </div>
      </div>

      {/* ─── Search Bar (only when no stock selected) ─── */}
      {!selectedStock && (
        <div className="sd-search-section" ref={suggestionsRef}>
          <div className="sd-search-wrap">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by Symbol, Token, or Company Name..."
              value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              id="stock-detail-search-initial"
            />
          </div>
          <button type="button" className="sd-btn-search" onClick={handleDirectSearch} disabled={loading || !searchQuery.trim()}>
            {loading ? "Searching..." : "Search"}
          </button>
          {searched && (
            <button type="button" className="sd-btn-clear" onClick={handleClear}>Clear</button>
          )}
          {showSuggestions && suggestions.length > 0 && (
            <div className="sd-suggestions">
              {suggestions.map((s, idx) => (
                <div key={`${s.symbol}-${idx}`} className="sd-suggestion-item" onClick={() => handleSelectStock(s)}>
                  <div className="sd-suggestion-icon">{(s.symbol || "?").slice(0, 3)}</div>
                  <div className="sd-suggestion-info">
                    <div className="sd-suggestion-symbol">{s.symbol}</div>
                    <div className="sd-suggestion-name">{s.name || "—"}</div>
                  </div>
                  <span className="sd-suggestion-exchange">{s.exchange || "NSE"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Error Message ──────────────────── */}
      {error && (
        <div className="sd-error">
          <span className="material-symbols-outlined">error</span>
          <span className="sd-error-text">{error}</span>
          <button className="sd-error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* ─── Loading State ──────────────────── */}
      {loading && (
        <div className="sd-loading">
          <div className="sd-spinner" />
          <p>Fetching stock details...</p>
        </div>
      )}

      {/* ─── Empty / Initial State ──────────── */}
      {!loading && !selectedStock && !error && (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">
            <span className="material-symbols-outlined">query_stats</span>
          </div>
          <h3>{searched ? "No Stock Found" : "Search for a Stock"}</h3>
          <p>
            {searched
              ? "No stock found with the provided symbol or name. Please check and try again."
              : "Enter a stock symbol (e.g., RELIANCE, TCS, INFY) or company name to view comprehensive market data, deals, deliveries, P/E ratios, announcements, and more."}
          </p>
        </div>
      )}

      {/* ═══════════════════════════════════════
           Stock Data Display
      ═══════════════════════════════════════ */}
      {selectedStock && !loading && (
        <>
          {/* ─── Hero Card ──────────────────── */}
          <div className="sd-hero-card">
            <div className="sd-hero-banner" />
            <div className="sd-hero-content">
              <div className="sd-hero-left">
                <div className="sd-hero-symbol-badge">
                  {(selectedStock.symbol || "?").slice(0, 3)}
                </div>
                <div className="sd-hero-info">
                  <div className="sd-hero-name-row">
                    <span className="sd-hero-symbol">{selectedStock.symbol}</span>
                    <span className="sd-hero-name">{selectedStock.name || "—"}</span>
                  </div>
                  <div className="sd-hero-meta">
                    <span className="sd-hero-chip exchange">{selectedStock.exch_seg || selectedStock.exchange || "NSE"}</span>
                    {selectedStock.instrumenttype && (
                      <span className="sd-hero-chip type">{selectedStock.instrumenttype}</span>
                    )}
                    {selectedStock.isin && (
                      <span className="sd-hero-chip isin">ISIN: {selectedStock.isin}</span>
                    )}
                    <span className="sd-hero-chip token">Token: {selectedStock.token || selectedStock.ticker}</span>
                    <span className={`sd-hero-chip ${selectedStock.tradeable !== false ? "tradeable" : "not-tradeable"}`}>
                      {selectedStock.tradeable !== false ? "● Tradeable" : "○ Not Tradeable"}
                    </span>
                  </div>
                </div>
                
                {/* LTP shifted to left side */}
                {currentLTP != null && (
                  <div className="sd-hero-ltp-inline" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '12px' }}>
                    <div className="sd-live-price" style={{ fontSize: '24px', margin: 0 }}>{formatCurrency(currentLTP)}</div>
                    {priceChange != null && (
                      <div className="sd-price-change" style={{ margin: 0 }}>
                        <span className={`sd-change-value ${changeDirection}`}>
                          {changeDirection === "up" ? "▲" : changeDirection === "down" ? "▼" : "●"} {priceChange >= 0 ? "+" : ""}{priceChange.toFixed(2)}
                        </span>
                        {priceChangePercent != null && (
                          <span className={`sd-change-percent ${changeDirection}`}>
                            ({priceChangePercent >= 0 ? "+" : ""}{priceChangePercent.toFixed(2)}%)
                          </span>
                        )}
                      </div>
                    )}
                    {livePrice && (
                      <div className="sd-live-indicator">
                        <span className="sd-live-dot" />
                        Live
                      </div>
                    )}
                    {marketStatus && (
                      <div className={`sd-market-status ${marketStatus.is_open ? "open" : "closed"}`} style={{ margin: 0 }}>
                        {marketStatus.is_open ? "● Market Open" : "● Market Closed"}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="sd-hero-right">
                {/* Search bar inside right column */}
                <div className="sd-hero-search-wrap" style={{ position: 'relative', width: '100%', maxWidth: '450px' }}>
                  <div className="sd-search-wrap" style={{ height: '44px', marginBottom: 0 }}>
                    <SearchIcon />
                    <input
                      type="text"
                      placeholder="Search by Symbol, Token..."
                      value={searchQuery}
                      onChange={(e) => handleSearchInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                      id="stock-detail-search-inline"
                      style={{ fontSize: '14px', background: '#fff' }}
                    />
                    <button
                      type="button"
                      className="sd-btn-search"
                      onClick={handleDirectSearch}
                      disabled={loading || !searchQuery.trim()}
                      style={{ padding: '0 16px', height: '36px', right: '4px', position: 'absolute', top: '4px' }}
                    >
                      {loading ? "..." : "Search"}
                    </button>
                    {searched && (
                      <button
                        type="button"
                        className="sd-btn-clear"
                        onClick={handleClear}
                        style={{ padding: '0 16px', height: '36px', right: '90px', position: 'absolute', top: '4px', background: 'transparent', color: '#6b7280', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  
                  {/* Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="sd-suggestions" style={{ top: '50px', zIndex: 100 }}>
                      {suggestions.map((s, idx) => (
                        <div key={`${s.symbol}-${idx}`} className="sd-suggestion-item" onClick={() => handleSelectStock(s)}>
                          <div className="sd-suggestion-icon">{(s.symbol || "?").slice(0, 3)}</div>
                          <div className="sd-suggestion-info">
                            <div className="sd-suggestion-symbol">{s.symbol}</div>
                            <div className="sd-suggestion-name">{s.name || "—"}</div>
                          </div>
                          <span className="sd-suggestion-exchange">{s.exchange || "NSE"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ─── Chart & Price Overview Row ────────────── */}
          <div className="sd-chart-overview-row">
            <div className="sd-chart-col">
              <TradingViewChart symbol={selectedStock.symbol} />
            </div>

            <div className="sd-overview-col">
              <Section title="Price Overview" icon="candlestick_chart" iconColor="green" className="sd-overview-section">
                <div className="sd-detail-grid two-col">
                  <DetailItem label="Open" value={formatCurrency(marketData?.open || selectedStock?.open)} />
                  <DetailItem label="High" value={formatCurrency(marketData?.high || selectedStock?.high)} />
                  <DetailItem label="Low" value={formatCurrency(marketData?.low || selectedStock?.low)} />
                  <DetailItem label="Close" value={formatCurrency(marketData?.close || selectedStock?.close)} />
                  <DetailItem label="Previous Close" value={formatCurrency(prevClose)} />
                  <DetailItem label="Last Price" value={formatCurrency(currentLTP)} />
                  <DetailItem label="Settlement Price" value={formatCurrency(marketData?.settlement_price)} />
                  <DetailItem label="LTP" value={formatCurrency(currentLTP)} large colorClass={changeDirection === "up" ? "green" : changeDirection === "down" ? "red" : ""} />
                </div>

                {/* Day's Visual Range Bar */}
                {(marketData?.high || selectedStock?.high) && (marketData?.low || selectedStock?.low) && (
                  <div className="sd-52w-range" style={{ marginTop: 'auto', paddingTop: '20px' }}>
                    <div className="sd-52w-labels">
                      <span className="sd-52w-label low">Day's Low</span>
                      <span className="sd-52w-label high">Day's High</span>
                    </div>
                    <div className="sd-52w-bar-container">
                      <div className="sd-52w-indicator" style={{ left: `${dayRangePosition}%` }} />
                    </div>
                    <div className="sd-52w-value-labels">
                      <span className="sd-52w-val low">{formatCurrency(marketData?.low || selectedStock?.low)}</span>
                      <span className="sd-52w-val high">{formatCurrency(marketData?.high || selectedStock?.high)}</span>
                    </div>
                  </div>
                )}
              </Section>
            </div>
          </div>

          {/* ─── Section Grid ─────────────────── */}
          <div className="sd-sections">

            {/* ─── Volume & Trading ────────────── */}
            <Section title="Volume & Trading Activity" icon="bar_chart" iconColor="blue">
              <div className="sd-detail-grid">
                <DetailItem label="Trade Volume" value={formatNumber(marketData?.trade_volume || selectedStock?.volume)} />
                <DetailItem label="Total Traded Value" value={marketData?.total_value ? `₹${formatNumber(marketData.total_value)}` : "—"} />
                <DetailItem label="Number of Trades" value={formatNumber(marketData?.trade_counts)} />
                <DetailItem label="Trade Date" value={formatDate(marketData?.trade_date)} />
              </div>
            </Section>

            {/* ─── 52-Week Range ───────────────── */}
            <Section title="52-Week Range" icon="trending_up" iconColor="orange">
              <div className="sd-detail-grid">
                <DetailItem label="52-Week High" value={formatCurrency(high52wData?.high_52w)} colorClass="green" />
                <DetailItem label="52-Week Low" value={formatCurrency(low52wData?.low_52w)} colorClass="red" />
                <DetailItem label="Prev High Date" value={formatDate(high52wData?.prev_high_date)} />
                <DetailItem label="Prev Low Date" value={formatDate(low52wData?.prev_low_date)} />
                <DetailItem label="Prev High Amt" value={formatCurrency(high52wData?.prev_high_amt)} />
                <DetailItem label="Prev Low Amt" value={formatCurrency(low52wData?.prev_low_amt)} />
              </div>

              {/* 52W Visual Range Bar */}
              {high52wData?.high_52w && low52wData?.low_52w && (
                <div className="sd-52w-range">
                  <div className="sd-52w-labels">
                    <span className="sd-52w-label low">52W Low</span>
                    <span className="sd-52w-label high">52W High</span>
                  </div>
                  <div className="sd-52w-bar-container">
                    <div className="sd-52w-indicator" style={{ left: `${range52wPosition}%` }} />
                  </div>
                  <div className="sd-52w-value-labels">
                    <span className="sd-52w-val low">{formatCurrency(low52wData.low_52w)}</span>
                    <span className="sd-52w-val high">{formatCurrency(high52wData.high_52w)}</span>
                  </div>
                </div>
              )}
            </Section>

            {/* ─── P/E Ratio ──────────────────── */}
            <Section title="P/E Ratio" icon="analytics" iconColor="purple">
              {peRatioData ? (
                <div className="sd-detail-grid">
                  <DetailItem label="Symbol P/E" value={peRatioData["symbol_p/e"] != null ? Number(peRatioData["symbol_p/e"]).toFixed(2) : "—"} large />
                  <DetailItem label="Adjusted P/E" value={peRatioData["adjusted_p/e"] != null ? Number(peRatioData["adjusted_p/e"]).toFixed(2) : "—"} large />
                  <DetailItem label="Trade Date" value={formatDate(peRatioData.trade_date)} />
                  <DetailItem label="Exchange" value={peRatioData.exchange || "NSE"} />
                </div>
              ) : (
                <div className="sd-no-data">No P/E ratio data available for this stock</div>
              )}
            </Section>

            {/* ─── Delivery Data ───────────────── */}
            <Section title="Delivery / Gross Deliverables" icon="local_shipping" iconColor="blue">
              {deliveryData ? (
                <>
                  <div className="sd-detail-grid">
                    <DetailItem label="Traded Quantity" value={formatNumber(deliveryData.traded_qty)} />
                    <DetailItem label="Traded Value" value={`₹${formatNumber(deliveryData.traded_value)}`} />
                    <DetailItem label="Delivery Quantity" value={formatNumber(deliveryData.delivery_qty)} />
                    <DetailItem label="Delivery Value" value={`₹${formatNumber(deliveryData.delivery_value)}`} />
                    <DetailItem label="Average Price" value={formatCurrency(deliveryData.average_price)} />
                    <DetailItem label="Trade Date" value={formatDate(deliveryData.trade_date)} />
                  </div>
                  {deliveryPct != null && (
                    <div className="sd-delivery-gauge">
                      <span className="sd-detail-label">Delivery Percentage</span>
                      <div className="sd-delivery-bar">
                        <div className="sd-delivery-fill" style={{ width: `${Math.min(100, deliveryPct)}%` }} />
                      </div>
                      <div className="sd-delivery-pct-label">{deliveryPct}%</div>
                    </div>
                  )}
                </>
              ) : (
                <div className="sd-no-data">No delivery data available for this stock</div>
              )}
            </Section>



            {/* ═══ Stock Analysis Widgets (from Equitty) ═══ */}

            {/* ─── SWOT Analysis (full-width) ─── */}
            <div className="sd-sa-full">
              {/* SWOT Analysis */}
              <div className="sa-widget sw-swot-widget">
                <h3 className="sa-widget-title" style={{ marginBottom: 16 }}>Analysis (SWOT)</h3>
                <div className="sw-swot-grid">
                  <div className="sw-swot-card strength">
                    <h4>Strength (11)</h4>
                    <div className="sw-swot-list">
                      <div>Strong buyer activity</div>
                      <div>Consistent dividend payer</div>
                      <div>Diversified revenue streams</div>
                    </div>
                    <button className="sw-view-more">View More</button>
                  </div>
                  <div className="sw-swot-card weakness">
                    <h4>Weakness (03)</h4>
                    <div className="sw-swot-list">
                      <div>High capex requirements</div>
                      <div>Margin pressure in retail</div>
                    </div>
                    <button className="sw-view-more">View More</button>
                  </div>
                  <div className="sw-swot-card opportunity">
                    <h4>Opportunity (07)</h4>
                    <div className="sw-swot-list">
                      <div>EV & Green energy expansion</div>
                      <div>Retail penetration growth</div>
                      <div>Digital services adoption</div>
                    </div>
                    <button className="sw-view-more">View More</button>
                  </div>
                  <div className="sw-swot-card threat">
                    <h4>Threat (02)</h4>
                    <div className="sw-swot-list">
                      <div>Oil price volatility</div>
                      <div>Regulatory changes</div>
                    </div>
                    <button className="sw-view-more">View More</button>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Fundamentals (full-width) ─── */}
            <div className="sd-sa-full">
              <div className="sa-widget sw-fundamentals-widget">
                <h3 className="sa-widget-title">Fundamentals</h3>
                <div className="sw-fund-wrapper">
                  <div className="sw-key-stats">
                    <div className="sw-key-stats-title">Key Statistics</div>
                    <div className="sw-stat-row"><span className="sw-stat-label">Market Cap</span><span className="sw-stat-val">₹2,401,179 Cr</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">P/E Ratio</span><span className="sw-stat-val">{peRatioData?.pe_ratio || "34.39"}</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">EPS</span><span className="sw-stat-val">₹65.30</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">Book Value</span><span className="sw-stat-val">₹289.40</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">Dividend Yield</span><span className="sw-stat-val">0.35%</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">Face Value</span><span className="sw-stat-val">₹1.00</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">ROE</span><span className="sw-stat-val">10.40%</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">ROCE</span><span className="sw-stat-val">22.50%</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">Debt to Equity</span><span className="sw-stat-val">0.45</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">Beta</span><span className="sw-stat-val">1.12</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">52wk High</span><span className="sw-stat-val">{high52wData?.high_52w ? formatCurrency(high52wData.high_52w) : "—"}</span></div>
                    <div className="sw-stat-row"><span className="sw-stat-label">52wk Low</span><span className="sw-stat-val">{low52wData?.low_52w ? formatCurrency(low52wData.low_52w) : "—"}</span></div>
                  </div>
                  <div>
                    <div className="sw-fin-high">
                      <div className="sw-key-stats-title">Financial Highlights</div>
                      <div className="sw-fin-high-row"><div className="sw-fin-label-wrap"><span className="sw-fin-label">Revenue</span><span className="sw-fin-sub">+15.3% YoY</span></div><span className="sw-fin-val">₹12,450 Cr</span></div>
                      <div className="sw-fin-high-row"><div className="sw-fin-label-wrap"><span className="sw-fin-label">Net Profit</span><span className="sw-fin-sub">+20.5% YoY</span></div><span className="sw-fin-val">₹2,340 Cr</span></div>
                      <div className="sw-fin-high-row"><div className="sw-fin-label-wrap"><span className="sw-fin-label">Operating Margin</span><span className="sw-fin-sub">+2.1% YoY</span></div><span className="sw-fin-val">24.5%</span></div>
                      <div className="sw-fin-high-row" style={{ borderBottom: 'none' }}><div className="sw-fin-label-wrap"><span className="sw-fin-label">Net Profit Margin</span><span className="sw-fin-sub">+15.0% YoY</span></div><span className="sw-fin-val">18.8%</span></div>
                    </div>
                    <div className="sw-share-pattern">
                      <div className="sw-key-stats-title">Shareholding Pattern</div>
                      <div className="sw-shr-list">
                        <div className="sw-shr-item"><div className="sw-shr-item-left"><div className="sw-shr-dot c-promoter" /> Promoters</div><div className="sw-shr-val">51.25%</div></div>
                        <div className="sw-shr-item"><div className="sw-shr-item-left"><div className="sw-shr-dot c-fii" /> FII</div><div className="sw-shr-val">22.49%</div></div>
                        <div className="sw-shr-item"><div className="sw-shr-item-left"><div className="sw-shr-dot c-dii" /> DII</div><div className="sw-shr-val">16.8%</div></div>
                        <div className="sw-shr-item"><div className="sw-shr-item-left"><div className="sw-shr-dot c-public" /> Public</div><div className="sw-shr-val">9.9%</div></div>
                      </div>
                      <div className="sw-shr-bar-wrapper">
                        <div className="sw-shr-segment c-promoter" style={{ width: '51.25%' }} />
                        <div className="sw-shr-segment c-fii" style={{ width: '22.49%' }} />
                        <div className="sw-shr-segment c-dii" style={{ width: '16.8%' }} />
                        <div className="sw-shr-segment c-public" style={{ width: '9.9%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Peer Comparison (full-width) ─── */}
            <div className="sd-sa-full">
              <div className="sa-widget sw-peer-widget">
                <h3 className="sa-widget-title" style={{ marginBottom: 20 }}>Peer Comparison</h3>
                <table className="sw-peer-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Movement</th>
                      <th>CMP</th>
                      <th>P/E</th>
                      <th>P/B</th>
                      <th>ROE</th>
                      <th>D/E</th>
                      <th>Rev. Gr.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { company: selectedStock?.symbol || "Stock", cmp: currentLTP ? formatCurrency(currentLTP) : "—", pe: peRatioData?.pe_ratio || "—", pb: "4.3", roe: "15.2%", de: "0.45", revGr: "10.5%", up: true },
                      { company: "HDFCBANK", cmp: "₹1,645.20", pe: "19.8", pb: "3.1", roe: "16.5%", de: "0.12", revGr: "12.3%", up: true },
                      { company: "TCS", cmp: "₹3,892.50", pe: "28.5", pb: "12.8", roe: "42.1%", de: "0.03", revGr: "8.7%", up: false },
                      { company: "INFY", cmp: "₹1,456.80", pe: "25.2", pb: "8.9", roe: "31.5%", de: "0.05", revGr: "6.2%", up: true },
                      { company: "ICICIBANK", cmp: "₹1,125.30", pe: "17.2", pb: "2.8", roe: "17.8%", de: "0.14", revGr: "15.1%", up: false },
                    ].map((p, i) => (
                      <tr key={i}>
                        <td>{p.company}</td>
                        <td>
                          <svg className="sw-peer-sparkline" viewBox="0 0 60 16" preserveAspectRatio="none">
                            {p.up ? (
                              <path d="M0 16 Q 10 10 20 12 T 40 4 T 60 2" fill="none" stroke="#16a34a" strokeWidth="1.5" />
                            ) : (
                              <path d="M0 2 C 20 2 30 10 40 8 S 50 14 60 16" fill="none" stroke="#dc2626" strokeWidth="1.5" />
                            )}
                          </svg>
                        </td>
                        <td>{p.cmp}</td>
                        <td>{p.pe}</td>
                        <td>{p.pb}</td>
                        <td>{p.roe}</td>
                        <td>{p.de}</td>
                        <td>{p.revGr}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── Performance + Volume Analysis (stacked) ─── */}
            <div className="sd-sa-row">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
                {/* Enhanced Performance */}
                <div className="sd-section">
                  <div className="sd-section-header">
                    <div className="sd-section-icon green">
                      <span className="material-symbols-outlined">trending_up</span>
                    </div>
                    <h3 className="sd-section-title">{selectedStock?.name || selectedStock?.symbol} Stock Performance</h3>
                  </div>
                  <div className="sd-section-body">
                    <div className="sd-vol-bars">
                      {[
                        { label: '1D', value: marketData?.change_pct || -1.25, isPerformance: true },
                        { label: '1W', value: 3.80, isPerformance: true },
                        { label: '1M', value: 9.45, isPerformance: true },
                        { label: '1Y', value: 42.6, isPerformance: true },
                        { label: '5Y', value: 59.6, isPerformance: true },
                        { label: '10Y', value: 89.6, isPerformance: true },
                      ].map((item, idx) => {
                        const maxVal = 100;
                        const absVal = Math.abs(item.value);
                        const barWidth = Math.min((absVal / maxVal) * 100, 100);
                        const isPositive = item.value >= 0;
                        return (
                          <div className="sd-vol-bar-row" key={idx}>
                            <span className="sd-vol-bar-label">{item.label}</span>
                            <div className="sd-vol-bar-track">
                              <div 
                                className={`sd-vol-bar-fill ${isPositive ? 'positive' : 'negative'}`} 
                                style={{ width: `${barWidth}%` }} 
                              />
                            </div>
                            <span className={`sd-vol-bar-value ${isPositive ? 'positive' : 'negative'}`}>
                              {isPositive ? '+' : ''}{item.value.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="sw-perf-stats" style={{ borderTop: '1px solid #f0f2f5', marginTop: 14 }}>
                      <div className="sw-perf-stat-col"><span className="sw-perf-stat-label">Open</span><span className="sw-perf-stat-val">{selectedStock?.open ? formatCurrency(selectedStock.open) : "—"}</span></div>
                      <div className="sw-perf-stat-col"><span className="sw-perf-stat-label">High</span><span className="sw-perf-stat-val">{selectedStock?.high ? formatCurrency(selectedStock.high) : "—"}</span></div>
                      <div className="sw-perf-stat-col"><span className="sw-perf-stat-label">Low</span><span className="sw-perf-stat-val">{selectedStock?.low ? formatCurrency(selectedStock.low) : "—"}</span></div>
                      <div className="sw-perf-stat-col"><span className="sw-perf-stat-label">Prev Close</span><span className="sw-perf-stat-val">{selectedStock?.prev_close ? formatCurrency(selectedStock.prev_close) : "—"}</span></div>
                      <div className="sw-perf-stat-col"><span className="sw-perf-stat-label">Volume</span><span className="sw-perf-stat-val">{selectedStock?.volume ? formatNumber(selectedStock.volume) : "—"}</span></div>
                      <div className="sw-perf-stat-col"><span className="sw-perf-stat-label">Avg Price</span><span className="sw-perf-stat-val">{selectedStock?.avg_price ? formatCurrency(selectedStock.avg_price) : "—"}</span></div>
                    </div>
                  </div>
                </div>

                {/* Volume Analysis */}
                <div className="sd-section">
                  <div className="sd-section-header">
                    <div className="sd-section-icon blue">
                      <span className="material-symbols-outlined">bar_chart</span>
                    </div>
                    <h3 className="sd-section-title">Volume Analysis</h3>
                  </div>
                  <div className="sd-section-body">
                    <div className="sd-vol-bars">
                      {(() => {
                        const todayVol = marketData?.trade_volume || selectedStock?.volume || 0;
                        const items = [
                          { label: 'Today', value: todayVol },
                          { label: 'Yesterday', value: todayVol * 1.18 },
                          { label: 'Day before yesterday', value: todayVol * 2.17 },
                          { label: '1 Wk Avg.', value: todayVol * 1.7 },
                          { label: '1 M Avg.', value: todayVol * 2.0 },
                          { label: '3 M Avg.', value: todayVol * 2.26 },
                        ];
                        const maxVol = Math.max(...items.map(i => i.value), 1);
                        return items.map((item, idx) => (
                          <div className="sd-vol-bar-row" key={idx}>
                            <span className="sd-vol-bar-label" style={{ width: 140 }}>{item.label}</span>
                            <div className="sd-vol-bar-track">
                              <div 
                                className="sd-vol-bar-fill volume" 
                                style={{ width: `${(item.value / maxVol) * 100}%` }} 
                              />
                            </div>
                            <span className="sd-vol-bar-value volume">{formatNumber(item.value)}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Price & Volume + Scans */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Price & Volume */}
                <div className="sa-widget sw-pv-widget" style={{ paddingBottom: '16px' }}>
                  <h3 className="sa-widget-title">Price & Volume</h3>
                  <div className="sw-pv-header-stats" style={{ marginBottom: '12px' }}>
                    <div className="sw-pv-stat">Today's Volume<strong>{selectedStock?.volume ? formatNumber(selectedStock.volume) : "—"}</strong></div>
                    <div className="sw-pv-stat">10D Avg Volume<strong>56 L</strong></div>
                    <div className="sw-pv-stat">Delivery %<strong>{deliveryPct ? `${deliveryPct}%` : "—"} <span className="sw-pv-badge">High</span></strong></div>
                    <div className="sw-pv-stat">Delivery Trend<strong>Increasing</strong></div>
                  </div>
                  <div className="sw-pv-chart-wrap" style={{ paddingTop: '10px' }}>
                    <svg width="100%" height="80" viewBox="0 0 300 80" preserveAspectRatio="none">
                      <line x1="0" y1="16" x2="300" y2="16" stroke="#e5e7eb" />
                      <line x1="0" y1="40" x2="300" y2="40" stroke="#e5e7eb" />
                      <line x1="0" y1="64" x2="300" y2="64" stroke="#e5e7eb" />
                      {[20, 60, 100, 140, 180, 220, 260].map((x, i) => (
                        <g key={i}>
                          <rect x={x} y={64 - (20 + i * 5)} width="4" height={20 + i * 5} fill={i % 2 === 0 ? '#16a34a' : '#dc2626'} />
                          <rect x={x + 6} y={64 - (10 + i * 2.5)} width="4" height={10 + i * 2.5} fill="var(--color-primary)" />
                        </g>
                      ))}
                      <path d="M 22 43 L 62 38 L 102 32 L 142 27 L 182 21 L 222 16 L 262 11" fill="none" stroke="#6b7280" strokeWidth="1" strokeDasharray="2 2" />
                    </svg>
                    <div className="sw-pv-legend" style={{ marginTop: '8px' }}>
                      <span className="sw-pv-legend-item"><div className="sw-pv-legend-dot" style={{ background: '#16a34a' }} /> Price close higher</span>
                      <span className="sw-pv-legend-item"><div className="sw-pv-legend-dot" style={{ background: '#dc2626' }} /> Price close lower</span>
                      <span className="sw-pv-legend-item"><div className="sw-pv-legend-dot" style={{ background: 'var(--color-primary)' }} /> Delivery Volume</span>
                    </div>
                  </div>
                </div>

                {/* Scans */}
                <div className="sa-widget sa-scans-widget">
                  <h3 className="sa-widget-title" style={{ marginBottom: 12 }}>Scans</h3>
                  {scanHitsData.length > 0 ? (
                    <div className="sw-scans-widget">
                      {(() => {
                        const getCategory = (s) => {
                          const tf = (s.scan_timeframe || '').toLowerCase();
                          if (tf.includes('day') || tf === '1d' || tf === 'daily') return 'Daily';
                          if (tf.includes('week') || tf === '1w' || tf === 'weekly') return 'Weekly';
                          if (tf.includes('month') || tf === '1m' || tf === 'monthly') return 'Monthly';
                          if (tf.includes('min') || tf.includes('hour')) return 'Intraday';
                          return 'Other';
                        };

                        // Compute Buy/Sell signal from scan directions
                        const bullishCount = scanHitsData.filter(s => (s.scan_direction || '').toLowerCase() === 'bullish').length;
                        const bearishCount = scanHitsData.filter(s => (s.scan_direction || '').toLowerCase() === 'bearish').length;
                        const neutralCount = scanHitsData.length - bullishCount - bearishCount;
                        const total = scanHitsData.length;
                        const bullishPct = total > 0 ? Math.round((bullishCount / total) * 100) : 50;
                        const bearishPct = total > 0 ? Math.round((bearishCount / total) * 100) : 50;
                        const neutralPct = total > 0 ? Math.round((neutralCount / total) * 100) : 0;
                        
                        // Determine overall signal
                        let signal = 'Neutral';
                        let signalColor = '#6b7280';
                        let signalBg = 'var(--bg-gray)';
                        if (bullishPct >= 70) { signal = 'Strong Buy'; signalColor = '#16a34a'; signalBg = '#dcfce7'; }
                        else if (bullishPct >= 55) { signal = 'Buy'; signalColor = '#16a34a'; signalBg = '#dcfce7'; }
                        else if (bearishPct >= 70) { signal = 'Strong Sell'; signalColor = '#dc2626'; signalBg = '#fee2e2'; }
                        else if (bearishPct >= 55) { signal = 'Sell'; signalColor = '#dc2626'; signalBg = '#fee2e2'; }
                        else { signal = 'Neutral'; signalColor = 'var(--color-warning)'; signalBg = 'var(--color-warning-bg)'; }

                        const groupedScans = scanHitsData.reduce((acc, s) => {
                          const cat = getCategory(s);
                          if (!acc[cat]) acc[cat] = [];
                          acc[cat].push(s);
                          return acc;
                        }, {});

                        const categoryOrder = ['Daily', 'Weekly', 'Monthly', 'Intraday', 'Other'];
                        const sortedCategories = Object.keys(groupedScans).sort((a, b) => {
                          let ia = categoryOrder.indexOf(a);
                          let ib = categoryOrder.indexOf(b);
                          if (ia === -1) ia = 99;
                          if (ib === -1) ib = 99;
                          return ia - ib;
                        });

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
                            {/* Consensus Verdict Gauge */}
                            <div className="sd-scan-gauge">
                              <div className="sd-scan-gauge-info">
                                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#16a34a' }}>check_circle</span>
                                <span style={{ fontSize: 12, color: '#6b7280' }}>Based on {total} scan{total !== 1 ? 's' : ''} analyzing technical patterns.</span>
                              </div>
                              <h4 className="sd-scan-gauge-heading">Consensus Verdict</h4>
                              <div className="sd-scan-gauge-signal-badge" style={{ color: signalColor, background: signalBg }}>
                                {signal}
                              </div>
                              <div className="sd-scan-gauge-arc-wrap">
                                <svg viewBox="0 0 200 110" className="sd-scan-gauge-svg">
                                  {/* Arc segments: Strong Sell, Sell, Hold, Buy, Strong Buy */}
                                  {/* Each segment is 36° of the 180° arc */}
                                  <path d="M 20 100 A 80 80 0 0 1 56 36" stroke="#ef4444" strokeWidth="14" fill="none" strokeLinecap="round" />
                                  <path d="M 58 34 A 80 80 0 0 1 100 20" stroke="#fb923c" strokeWidth="14" fill="none" />
                                  <path d="M 100 20 A 80 80 0 0 1 142 34" stroke="#facc15" strokeWidth="14" fill="none" />
                                  <path d="M 144 36 A 80 80 0 0 1 180 100" stroke="#22c55e" strokeWidth="14" fill="none" strokeLinecap="round" />
                                  {/* Needle */}
                                  {(() => {
                                    // Map bullishPct to angle: 0% = 180° (Strong Sell), 100% = 0° (Strong Buy)
                                    const angle = 180 - (bullishPct / 100) * 180;
                                    const rad = (angle * Math.PI) / 180;
                                    const cx = 100, cy = 100, r = 55;
                                    const nx = cx + r * Math.cos(rad);
                                    const ny = cy - r * Math.sin(rad);
                                    return (
                                      <>
                                        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" />
                                        <circle cx={cx} cy={cy} r="5" fill="var(--color-primary)" />
                                        <circle cx={cx} cy={cy} r="2.5" fill="#fff" />
                                      </>
                                    );
                                  })()}
                                </svg>
                              </div>
                              <div className="sd-scan-gauge-bottom">
                                <div className="sd-scan-gauge-chip">Strong Sell<span>{bearishPct >= 70 ? bearishCount : 0}</span></div>
                                <div className="sd-scan-gauge-chip">Sell<span>{bearishPct >= 55 && bearishPct < 70 ? bearishCount : 0}</span></div>
                                <div className="sd-scan-gauge-chip">Hold<span>{neutralCount}</span></div>
                                <div className="sd-scan-gauge-chip">Buy<span>{bullishPct >= 55 && bullishPct < 70 ? bullishCount : 0}</span></div>
                                <div className="sd-scan-gauge-chip">Strong Buy<span>{bullishPct >= 70 ? bullishCount : 0}</span></div>
                              </div>
                            </div>

                            {sortedCategories.map((cat, idx) => (
                              <div key={`cat-${idx}`} className="sw-scans-col" style={{ width: '100%' }}>
                                <div className="sw-scans-col-label" style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '6px', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-body)', textTransform: 'uppercase' }}>
                                  {cat} Scans
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                  <thead>
                                    <tr style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--bg-gray)', textAlign: 'left' }}>
                                      <th style={{ padding: '6px 0', fontWeight: 600, width: '50%' }}>Scan Name</th>
                                      <th style={{ padding: '6px 0', fontWeight: 600, textAlign: 'center', width: '25%' }}>Pattern</th>
                                      <th style={{ padding: '6px 0', fontWeight: 600, textAlign: 'right', width: '25%' }}>Hit Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupedScans[cat].map((s, i) => (
                                      <tr key={`scan-${i}`} style={{ borderBottom: '1px solid #f9fafb' }}>
                                        <td style={{ padding: '8px 0', fontWeight: 500, color: '#111827' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div className="sw-scan-check" style={{ 
                                              background: (s.scan_direction || '').toLowerCase() === 'bearish' ? '#fee2e2' : '#dcfce7', 
                                              color: (s.scan_direction || '').toLowerCase() === 'bearish' ? '#dc2626' : '#16a34a',
                                              width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', flexShrink: 0
                                            }}>✓</div>
                                            <span style={{ wordBreak: 'break-word', lineHeight: 1.2 }}>{s.scan_name}</span>
                                          </div>
                                        </td>
                                        <td style={{ padding: '8px 0', textAlign: 'center' }}>
                                          {s.scan_direction ? (
                                            <span style={{ 
                                              fontSize: '10px', 
                                              padding: '3px 8px', 
                                              borderRadius: '12px', 
                                              background: (s.scan_direction || '').toLowerCase() === 'bullish' ? '#dcfce7' : (s.scan_direction || '').toLowerCase() === 'bearish' ? '#fee2e2' : 'var(--bg-gray)', 
                                              color: (s.scan_direction || '').toLowerCase() === 'bullish' ? '#16a34a' : (s.scan_direction || '').toLowerCase() === 'bearish' ? '#dc2626' : '#6b7280', 
                                              fontWeight: 600,
                                              display: 'inline-block'
                                            }}>
                                              {s.scan_direction.charAt(0).toUpperCase() + s.scan_direction.slice(1).toLowerCase()}
                                            </span>
                                          ) : '—'}
                                        </td>
                                        <td style={{ padding: '8px 0', textAlign: 'right', color: '#6b7280' }}>
                                          {s.scan_timestamp ? new Date(s.scan_timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.5 }}>search_off</span>
                      No scan hits found for this stock
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ─── Trade Recommendations History ─── */}
            <Section title="Trade Recommendations History" icon="show_chart" iconColor="blue" fullWidth>
              {tradeRecommendationsData.length > 0 ? (
                <div className="sd-table-container">
                  <table className="sd-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th className="col-right">Entry (Avg)</th>
                        <th className="col-right">Current/Exit Price</th>
                        <th className="col-right">T1</th>
                        <th className="col-right">T2</th>
                        <th className="col-right">SL</th>
                        <th className="col-right">Actual Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradeRecommendationsData.map((trade, idx) => {
                        const entryPrice = trade.recoPriceLow && trade.recoPriceHigh 
                          ? (Number(trade.recoPriceLow) + Number(trade.recoPriceHigh)) / 2
                          : Number(trade.recoPriceLow) || Number(trade.recoPriceHigh) || 0;
                        
                        const isLive = String(trade.status || "").toLowerCase() === "live";
                        const isClosed = String(trade.status || "").toLowerCase() === "closed";
                        
                        const currentExitPrice = isLive ? Number(trade.ltp || 0) : Number(trade.exitPriceLow || 0);

                        return (
                          <tr key={idx}>
                            <td>{formatDate(trade.createdAt || trade.recoDate)}</td>
                            <td><span className="sd-badge info">{trade.tradeTypeName || "—"}</span></td>
                            <td>
                              <span className={`sd-badge ${isLive ? "buy" : isClosed ? "sell" : "neutral"}`}>
                                {trade.status || "—"}
                              </span>
                            </td>
                            <td className="col-right col-mono">₹{formatNumber(entryPrice)}</td>
                            <td className="col-right col-mono">
                              {currentExitPrice ? `₹${formatNumber(currentExitPrice)}` : "—"}
                            </td>
                            <td className="col-right col-mono" style={{ color: trade.targetOneStatus ? "#16a34a" : "inherit" }}>
                              {trade.targetOne ? `₹${formatNumber(trade.targetOne)}` : "—"}
                            </td>
                            <td className="col-right col-mono" style={{ color: trade.targetTwoStatus ? "#16a34a" : "inherit" }}>
                              {trade.targetTwo ? `₹${formatNumber(trade.targetTwo)}` : "—"}
                            </td>
                            <td className="col-right col-mono" style={{ color: trade.status?.toLowerCase().includes("stoploss") ? "#dc2626" : "inherit" }}>
                              {trade.stopLoss ? `₹${formatNumber(trade.stopLoss)}` : "—"}
                            </td>
                            <td className="col-right col-mono">
                              {trade.actual_profit != null ? (
                                <span style={{ color: Number(trade.actual_profit) >= 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                                  {Number(trade.actual_profit) > 0 ? "+" : ""}{Number(trade.actual_profit).toFixed(2)}%
                                </span>
                              ) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="sd-no-data">No trade recommendations found for this stock</div>
              )}
            </Section>

            {/* ─── Deals (Block/Bulk) ──────────── */}
            <Section title="Deals (Block / Bulk)" icon="handshake" iconColor="green" fullWidth>
              {dealsData.length > 0 ? (
                <>
                  <div className="sd-table-container">
                    <table className="sd-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Client</th>
                          <th>Buy/Sell</th>
                          <th className="col-right">Quantity</th>
                          <th className="col-right">Price</th>
                          <th className="col-right">Value</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDeals.map((deal, idx) => (
                          <tr key={idx}>
                            <td>{formatDate(deal.trade_date)}</td>
                            <td>
                              <span className={`sd-badge ${(deal.deal_type || "").toLowerCase().includes("block") ? "warning" : "info"}`}>
                                {deal.deal_type || "—"}
                              </span>
                            </td>
                            <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {deal.client_name || "—"}
                            </td>
                            <td>
                              <span className={`sd-badge ${deal.buy_sell?.toLowerCase() === "buy" ? "buy" : "sell"}`}>
                                {deal.buy_sell || "—"}
                              </span>
                            </td>
                            <td className="col-right col-mono">{formatNumber(deal.quantity_traded)}</td>
                            <td className="col-right col-mono">{formatCurrency(deal.trade_price)}</td>
                            <td className="col-right col-mono">{deal.value ? `₹${formatNumber(deal.value)}` : "—"}</td>
                            <td style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {deal.remarks || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalDealsPages > 1 && (
                    <div className="sd-pagination">
                      <button disabled={dealsPage <= 1} onClick={() => setDealsPage(p => p - 1)}>← Prev</button>
                      <span>Page {dealsPage} of {totalDealsPages}</span>
                      <button disabled={dealsPage >= totalDealsPages} onClick={() => setDealsPage(p => p + 1)}>Next →</button>
                    </div>
                  )}
                </>
              ) : (
                <div className="sd-no-data">No block/bulk deals data available for this stock</div>
              )}
            </Section>

            {/* ─── News & Announcements Grid ───────────────── */}
            <div className="sd-news-announcements-grid">
              <StockNewsWidget newsItems={newsItems} loading={loading} />
              <StockAnnouncementsWidget events={announcements} loading={loading} />
            </div>

            {/* ─── About the Company (full-width) ─── */}
            <div className="sd-sa-full">
              <div className="sa-widget sw-about-company-widget">
                <h3 className="sa-widget-title">About the Company</h3>
                <div className="sw-about-top-grid">
                  <div className="sw-ac-box">
                    <h4 className="sw-ac-box-title">{selectedStock?.symbol || "Company"}</h4>
                    <div className="sw-ac-stats-grid">
                      <div className="sw-ac-stat"><span>Founded:</span> 1966</div>
                      <div className="sw-ac-stat"><span>Index Member:</span> NIFTY 50, SENSEX</div>
                      <div className="sw-ac-stat"><span>Sector:</span> {selectedStock?.sector || "—"}</div>
                      <div className="sw-ac-stat"><span>Promoter Holding:</span> 50.3%</div>
                      <div className="sw-ac-stat"><span>Promoter:</span> —</div>
                      <div className="sw-ac-stat"><span>Public & Institutional:</span> 49.7%</div>
                      <div className="sw-ac-stat"><span>Market Cap:</span> ₹XX Lakh Cr</div>
                      <div className="sw-ac-stat"><span>Control:</span> Promoter Group</div>
                    </div>
                  </div>
                  <div className="sw-ac-box sw-ac-box-flex">
                    <h4 className="sw-ac-box-title">Business Segments</h4>
                    <div className="sw-pie-chart-wrap">
                      <div className="sw-pie-legends">
                        <div className="sw-pie-leg"><span style={{ background: 'var(--color-primary)' }} /> Energy & Petrochemicals</div>
                        <div className="sw-pie-leg"><span style={{ background: '#16a34a' }} /> Retail</div>
                        <div className="sw-pie-leg"><span style={{ background: '#f59e0b' }} /> Digital Services</div>
                      </div>
                      <div className="sw-pie-wrap">
                        <div className="sw-pie sw-pie-sm" />
                        <span className="sw-pie-label sw-pie-label-top">52%</span>
                        <span className="sw-pie-label sw-pie-label-mid">30%</span>
                        <span className="sw-pie-label sw-pie-label-bot">18%</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sw-about-top-grid">
                  <div className="sw-ac-box sw-about-text">
                    <p>This company is listed on the Indian stock exchanges and operates across diversified business interests. Further details about the company's operations, management, and strategy are available in its annual reports.</p>
                    <p>The company has been focusing on building future-ready businesses including renewable energy, digital ecosystems, and expanding its retail footprint across India.</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div className="sw-ac-box">
                      <h5 className="sw-ac-box-title">Corporate Details</h5>
                      <div className="sw-corp-item">Registered Office</div>
                      <div className="sw-corp-item"><strong>—</strong></div>
                      <div className="sw-corp-item">Tel. No. : <strong>—</strong></div>
                      <div className="sw-corp-item">Email : <strong>—</strong></div>
                    </div>
                    <div className="sw-ac-box">
                      <h5 className="sw-ac-box-title">Stock Details</h5>
                      <div className="sw-stock-details-grid">
                        <div><div className="sw-corp-item">BSE Code</div><div className="sw-corp-val">{selectedStock?.bse_code || "—"}</div></div>
                        <div><div className="sw-corp-item">NSE Symbol</div><div className="sw-corp-val">{selectedStock?.symbol || "—"}</div></div>
                        <div><div className="sw-corp-item">Series</div><div className="sw-corp-val">EQ</div></div>
                        <div><div className="sw-corp-item">ISIN</div><div className="sw-corp-val">{selectedStock?.isin || "—"}</div></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
};

export default StockDetailsPage;

